/**
 * Breadcrumb navigation component
 *
 * Displays hierarchical navigation breadcrumbs with entity name resolution
 * and folder hierarchy support.
 */

import React from 'react';
import { Link } from '@tanstack/react-router';
import { Home, Trash2 } from 'lucide-react';
import type { WorkspaceItemType } from '@loader/shared-types';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Skeleton,
} from '@loader/ui';


import { useBreadcrumbItems } from './useBreadcrumbItems';
import type { BreadcrumbItemData } from './breadcrumbUtils';

import { SimpleWorkspaceIcon } from '@/components/icons';

interface BreadcrumbNavProps {
  items?: BreadcrumbItemData[];
  className?: string;
}

/**
 * Render icon for breadcrumb item
 */
function renderIcon(iconType?: 'workspace' | 'trash' | WorkspaceItemType) {
  if (!iconType) return null;
  if (iconType === 'workspace') {
    return <Home className="size-4 text-muted-foreground" />;
  }
  if (iconType === 'trash') {
    return <Trash2 className="size-4 text-muted-foreground" />;
  }
  return <SimpleWorkspaceIcon type={iconType} />;
}

export function BreadcrumbNav({ items, className }: BreadcrumbNavProps) {
  const { breadcrumbItems, isLoading } = useBreadcrumbItems(items);

  return (
    <Breadcrumb className={className}>
      <BreadcrumbList>
        {isLoading && breadcrumbItems.length === 0 ? (
          <BreadcrumbItem>
            <Skeleton className="h-4 w-24" />
          </BreadcrumbItem>
        ) : (
          breadcrumbItems.map((item, index) => (
            <React.Fragment key={index}>
              {index > 0 && <BreadcrumbSeparator className="hidden md:block" />}
              <BreadcrumbItem className="hidden md:block">
                {item.isLoading ? (
                  <Skeleton className="h-4 w-32" />
                ) : index === breadcrumbItems.length - 1 ? (
                  <BreadcrumbPage className="flex items-center gap-1.5">
                    {renderIcon(item.iconType)}
                    {item.label}
                  </BreadcrumbPage>
                ) : (
                  <BreadcrumbLink asChild className="flex items-center gap-1.5">
                    <Link to={item.href ?? '#'}>
                      {renderIcon(item.iconType)}
                      {item.label}
                    </Link>
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
            </React.Fragment>
          ))
        )}
      </BreadcrumbList>
    </Breadcrumb>
  );
}
